import React from "react";
import { motion } from "framer-motion";
import {
  Library,
  BookOpen,
  Users,
  ShieldCheck,
  GraduationCap,
  ClipboardCheck,
  Monitor,
  Building2,
  ArrowRight,
} from "lucide-react";

const FacilitiesSection = () => {
  const facilities = [
    {
      icon: <Library size={26} />,
      title: "Library Facility",
      desc: "A useful collection of books and study materials for students.",
    },
    {
      icon: <BookOpen size={26} />,
      title: "Quality Education",
      desc: "Focused teaching environment for better academic performance.",
    },
    {
      icon: <Users size={26} />,
      title: "Experienced Faculty",
      desc: "Supportive teachers guiding students with proper attention.",
    },
    {
      icon: <ShieldCheck size={26} />,
      title: "Disciplined Campus",
      desc: "Safe, positive, and value-based campus environment.",
    },
    {
      icon: <Monitor size={26} />,
      title: "Modern Learning",
      desc: "Student-focused learning approach with updated academic support.",
    },
    {
      icon: <ClipboardCheck size={26} />,
      title: "Academic Guidance",
      desc: "Regular guidance for admission, courses, and student progress.",
    },
  ];
  return (
    <section className="w-full bg-white py-12 md:py-14 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <span className="inline-flex items-center gap-2 bg-[#071A52] text-[#FFD700] px-5 py-2 rounded-full text-xs font-black uppercase tracking-wide mb-4 shadow">
              <Building2 size={15} />
              College Facilities
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#071A52] leading-tight">
              Facilities for Better Learning
            </h2>
            <div className="w-24 h-1.5 bg-[#FFD700] rounded-full mx-auto mt-4 mb-4"></div>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Pt. Shrinivas Dwivedi Mahavidyalaya provides essential academic
              facilities to support student learning, discipline, and overall
              development.
            </p>
          </motion.div>
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }}
              viewport={{ once: true }}
              className="lg:col-span-4"
            >
              <div className="h-full bg-gradient-to-br from-[#071A52] via-[#0B1F5C] to-[#102B75] rounded-[30px] p-7 md:p-8 text-white shadow-xl relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-52 h-52 bg-[#FFD700]/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-[#FFD700] text-[#071A52] flex items-center justify-center mb-6">
                    <GraduationCap size={34} />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black leading-tight mb-4">
                    Student-Friendly Campus
                  </h3>
                  <p className="text-white/75 text-sm leading-relaxed mb-6">
                    Our facilities are designed to create a supportive,
                    disciplined, and academic-focused environment for students.
                  </p>
                  <div className="space-y-3">
                    {[
                      "Learning Support",
                      "Discipline & Values",
                      "Academic Environment",
                      "Student Development",
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-xl px-4 py-3"
                      >
                        <ArrowRight size={16} className="text-[#FFD700]" />
                        <span className="text-sm font-semibold">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {facilities.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="group bg-[#F6F8FC] rounded-[24px] p-5 md:p-6 border border-gray-100 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#071A52] text-[#FFD700] flex items-center justify-center mb-5 group-hover:bg-[#FFD700] group-hover:text-[#071A52] transition-all">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-black text-[#071A52] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-8 bg-[#F6F8FC] rounded-[26px] p-5 md:p-6 shadow-md border border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h3 className="text-xl md:text-2xl font-black text-[#071A52]">
                Supportive Facilities for Every Student
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                Facilities that help students learn better and grow with
                confidence.
              </p>
            </div>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#071A52] text-[#FFD700] px-6 py-3 rounded-xl text-sm font-black hover:bg-[#FFD700] hover:text-[#071A52] transition-all w-fit"
            >
              Contact College
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
export default FacilitiesSection;